import { FC, useEffect, useMemo, useRef, useState } from 'react';
import cs from 'classnames';
import dayjs from 'dayjs';
import { useMethods } from '@lhb/hook';
import { beautifyThePrice, isEqual, isNotEmpty, isNotEmptyAny, replaceEmpty } from '@lhb/func';
import { useDebounceEffect } from '@/common/hook/useEffectDebounce';
import { attentionBrandTop, getShareYouthMapIndustryInsuranceVolumeLatestDate, getYouthMapIndustryInsuranceVolume, getYouthMapIndustryInsuranceVolumeLatestDate, getYouthMapIndustryInsuranceVolumeNational, postShareYouthMapIndustryInsuranceVolume, postShareYouthMapIndustryInsuranceVolumeNational } from '@/common/api/youth';
import styles from './index.module.less';
import { Spin, Typography } from 'antd';
import Title from '../Title';
import IconFont from '@/common/components/Base/IconFont';
import DateSelect from './DateSelect';
import BrandListChartModal from './BrandListChartModal';
import BrandListChart from './BrandListChart';
import V2Title from '@/common/components/Feedback/V2Title';
import { postShareCombatMapQueryTendencyV2 } from '@/common/api/spotevaluation';

/**
 * 选址地图左侧-汽车上险量
 * @description 城市/省份/分公司/全国视角、竞品品牌筛选，点位测评详情-城市信息
 * @demo
 */
const Component:FC<{
  filter: any,
  /** 是否展示品牌列表 */
  showBrandList?: boolean,
  /** 是否在点位测评使用 */
  inSpotevaluation?: boolean,
  className?: any,
  /** 租户Id，分享使用 */
  tenantId?: any,
   /** 用户Id，分享使用 */
  userId?: any,
  // 标题
  titleStr?: string;
}> = ({
  filter = {},
  showBrandList = true,
  inSpotevaluation = false,
  tenantId,
  userId,
  titleStr,
}) => {

  /* state */
  const [loading, setLoading] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<any>([]);
  const [data, setData] = useState<any>(null);
  const [brandListData, setBrandListData] = useState<any>([]);
  const carListChartModalRef = useRef<any>(null);

  const [lastSearchParams, setLastSearchParams] = useState<any>({});

  const _showBrandList = showBrandList && Array.isArray(data?.brandListData) && data.brandListData.length > 0;

  /** 缓存，避免频繁绘制图表 */
  const brandListLimitData = useMemo(() => {
    return Array.isArray(data?.brandListData) ? data.brandListData.slice(0, 10) : [];
  }, [data]);

  /** 避免频繁更新接口 */
  const searchParams = useMemo(() => {
    return {
      provinceId: filter?.province?.id,
      cityId: filter?.city?.id,
      /** 分公司 */
      branchCompanyId: filter?.company?.id,
      /** 竞品品牌 */
      competitorBrandIds: brandListData?.map?.((item) => item.brandId)?.filter(Boolean),
    };
  }, [filter?.province, filter?.city, brandListData]);

  /* hooks */
  useEffect(() => {
    methods.getLatestDate();
    methods.getBrandList();
  }, []);
  useEffect(() => {
    methods.init();
  }, [dateRange, showBrandList]);
  useDebounceEffect(() => {
    /** 避免频繁更新接口 */
    if (!isEqual(lastSearchParams, searchParams)) {
      setLastSearchParams(searchParams);
      methods.init();
    }
  }, [searchParams], 200);

  /* methods */

  const methods = useMethods({

    init() {
      // console.log('---init---');
      methods.loadData();
    },

    /** 获取最新生效日期 */
    getLatestDate() {
      const api = tenantId ? getShareYouthMapIndustryInsuranceVolumeLatestDate : getYouthMapIndustryInsuranceVolumeLatestDate;
      const params = tenantId ? {
        tenantId,
        cityId: searchParams.cityId,
        userId
      } : {};
      api(params).then((res) => {
        if (res?.startTime && res?.endTime) {
          setDateRange([dayjs(res.startTime).format('YYYY-MM'), dayjs(res.endTime).format('YYYY-MM')]);
        }
      });
    },

    /** 获取品牌列表 */
    getBrandList() {
      const api = tenantId ? postShareCombatMapQueryTendencyV2 : attentionBrandTop;
      const params = tenantId ? {
        tenantId,
        cityId: searchParams.cityId,
        userId
      } : {};
      api(params).then((res) => {
        setBrandListData(Array.isArray(res) ? res : []);
      });
    },

    loadData() {
      if (!dateRange || dateRange.length !== 2) return;
      const params1 = {
        provinceId: searchParams.provinceId,
        cityId: searchParams.cityId,
        branchCompanyId: searchParams.branchCompanyId, // 分公司
        startMonth: dateRange[0],
        endMonth: dateRange[1],
        ...(tenantId ? { tenantId } : {}),
        ...(userId ? { userId } : {})
      };
      const params2 = {
        provinceId: searchParams.provinceId,
        cityId: searchParams.cityId,
        branchCompanyId: searchParams.branchCompanyId, // 分公司
        brandIds: searchParams.competitorBrandIds, // 竞品品牌
        startMonth: dateRange[0],
        endMonth: dateRange[1],
        ...(tenantId ? { tenantId } : {}),
        ...(userId ? { userId } : {})
      };

      setLoading(true);
      Promise.all([
        tenantId ? postShareYouthMapIndustryInsuranceVolumeNational(params1) : getYouthMapIndustryInsuranceVolumeNational(params1),
        showBrandList && Array.isArray(searchParams.competitorBrandIds) && searchParams.competitorBrandIds.length > 0 ? tenantId ? postShareYouthMapIndustryInsuranceVolume(params2) : getYouthMapIndustryInsuranceVolume(params2) : () => [],
      ]).then(([res1, res2]) => {
        setData({
          statisticalData: isNotEmptyAny(res1) ? [
            { label: '上险量(PV)', value: isNotEmpty(res1.nationalInsuranceVolume) ? beautifyThePrice(res1.nationalInsuranceVolume, ',', 'auto') : null },
            { label: '市占率(PV)', value: isNotEmpty(res1.nationalMarketShare) ? `${res1.nationalMarketShare}%` : null },
            {
              label: res1.selfBrandName,
              value: isNotEmpty(res1.selfBrandInsuranceVolume) ? beautifyThePrice(res1.selfBrandInsuranceVolume, ',', 'auto') : null,
              className: styles.selfBrandInsuranceVolume,
            },
          ] : [],
          brandListData: Array.isArray(res2) ? res2.map(item => ({ label: item.brandName, value: item.insuranceVolume || 0 })) : [],
        });
      }).finally(() => {
        setLoading(false);
      });

    },

    /** 日期范围改变 */
    changeDateRange(value: string[]) {
      setDateRange(value);
      // methods.init();
    },

    /** 查看更多汽车品牌 */
    showMoreCarList() {
      carListChartModalRef.current?.init();
    },

  });

  return (data ? <div className={cs(styles.carInsuranceAmountWrapper)}>
    {inSpotevaluation
      ? <V2Title
        type='H2'
        text='城市上险量'
        className={styles.title}
        extra={<DateSelect dateRange={dateRange} onChange={methods.changeDateRange} />}/>
      : <Title
        style={{ paddingTop: '16px' }}
        icon={{ iconHref: 'asset-pc-dianweiceping_jiache' }}
        rightSlot={<DateSelect dateRange={dateRange} onChange={methods.changeDateRange} />}
      >{titleStr || '汽车上险量'}</Title>}
    <Spin spinning={loading}>
      <div className={cs(styles.carInsuranceAmount, inSpotevaluation && styles.carInsuranceAmountInSpotevaluation)}>
        {Array.isArray(data.statisticalData) && data.statisticalData.length > 0 && <div className={styles.statisticalData}>
          {data.statisticalData.map((item, index) => <StatisticsItem key={index} value={item.value} unit={item.unit} label={item.label} className={item.className} />)}
        </div>}

        {_showBrandList && <div className={styles.brandList}>
          <BrandListChart listData={brandListLimitData} />
          {data.brandListData.length > 10 && <div className={styles.showMore} onClick={methods.showMoreCarList}>
            <span>查看更多</span>
            <IconFont iconHref='iconic_next_black_seven' className={cs(styles.icon, 'fs-10')} />
          </div>}
        </div>}
      </div>
    </Spin>

    <BrandListChartModal ref={carListChartModalRef} listData={data.brandListData} />
  </div> : <></>);
};

/**
 * 统计数据块
 * @description
 * @demo
 */
const StatisticsItem:FC<{
  value?: any;
  unit?: string;
  label: string;
  className?: string,
}> = ({ value, unit, label, className }) => {

  /* state */

  /* hooks */

  /* methods */

  return (<div className={cs(styles.statisticsItem, className)}>
    <div className={styles.valueWrapper}>
      <span className={cs(styles.value, 'value')}>{replaceEmpty(value as any)}</span>
      {isNotEmpty(value) && !!unit && <span className={styles.unit}>{unit}</span>}
    </div>
    <div className={styles.labelWrapper}>
      <Typography.Text className={cs(styles.label, 'label')} ellipsis={{ tooltip: true }}>{label}</Typography.Text>
    </div>
  </div>);
};

export default Component;
